// "Your account" (docs/plans/account-page.md): the signed-in person's own page,
// opened from the account menu. It is about the person, never about a business:
// their name, their password, the businesses they belong to, and signing out.
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  PageHeader,
  PasswordInput,
  PasswordStrength,
  useToast,
} from '@ims/ui'
import { CloudOff, KeyRound, LogOut } from '@ims/ui/icons'
import { type FormEvent, useState } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage, useOnline } from '../hooks.ts'
import {
  NAME_MAX,
  accountName,
  cleanName,
  displayName,
  initialsOf,
  nameProblem,
  roleSentence,
} from '../lib/account.ts'
import { checkPassword, strengthOf } from '../lib/password.ts'
import { useSignOut } from '../layout/sign-out.tsx'
import { WrongPasswordError, useSession, useUser } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { SettingsPart } from './settings-part.tsx'

export function AccountScreen() {
  const user = useUser()
  const online = useOnline()
  const signOut = useSignOut()
  const [changing, setChanging] = useState(false)
  const email = user.email ?? ''

  return (
    <div>
      <PageHeader
        title="Your account"
        subtitle="Your name, your password and the businesses you belong to."
      />
      <Card className="p-0 md:max-w-[900px]">
        <SettingsPart
          title="Profile"
          about="Who you are to your team. The same in every business you belong to."
        >
          {!online && (
            <p
              role="status"
              className="flex gap-2 rounded-xl bg-warning-light px-3 py-2 text-xs text-warning sm:col-span-2"
            >
              <CloudOff className="mt-px size-4 shrink-0" aria-hidden />
              You are offline. Your name and password can be changed when the internet is back.
            </p>
          )}
          <div className="flex items-center gap-3.5 sm:col-span-2" translate="no">
            <span
              aria-hidden
              className="flex size-14 shrink-0 items-center justify-center rounded-full bg-primary-light text-lg font-bold text-primary"
            >
              {initialsOf(user)}
            </span>
            <div className="min-w-0">
              <b className="block truncate text-base tracking-tight">{displayName(user)}</b>
              <span className="block truncate text-sm text-muted">{email}</span>
            </div>
          </div>
          {/* A saved name starts the form again from it. */}
          <NameForm key={accountName(user) ?? ''} online={online} />
        </SettingsPart>
        <SettingsPart
          title="Password"
          about="Ten characters or more. You will stay signed in on this device."
        >
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button
              variant="secondary"
              disabled={!online}
              icon={<KeyRound className="size-4" aria-hidden />}
              onClick={() => setChanging(true)}
            >
              Change password
            </Button>
            <span className="text-xs text-muted">
              Forgot it? Sign out and choose “Forgot password” to get a code by email.
            </span>
          </div>
        </SettingsPart>
        <SettingsPart
          title="Your businesses"
          about="Every business you can open, and what you are in each. An owner or admin changes roles in Settings → Team."
        >
          <Businesses />
        </SettingsPart>
        <SettingsPart
          title="Sign out"
          about="Removes this device’s copy of your businesses. Anything not yet synced is listed first."
        >
          <div className="sm:col-span-2">
            <Button
              variant="secondary"
              className="text-danger!"
              loading={signOut.busy}
              icon={<LogOut className="size-4" aria-hidden />}
              onClick={() => void signOut.start()}
            >
              Sign out
            </Button>
          </div>
        </SettingsPart>
      </Card>
      {changing && <PasswordDialog onClose={() => setChanging(false)} />}
      {signOut.dialog}
    </div>
  )
}

function NameForm({ online }: { online: boolean }) {
  const user = useUser()
  const { updateName } = useSession()
  const toast = useToast()
  const saved = accountName(user) ?? ''
  const [name, setName] = useState(saved)
  const [busy, setBusy] = useState(false)
  const changed = cleanName(name) !== saved
  const problem = changed ? nameProblem(name) : null

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!changed || problem) return
    setBusy(true)
    try {
      await updateName(cleanName(name))
      toast.success('Name saved')
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="contents">
      <Field
        label="Your name"
        hint="Shown on the Team list and in Activity."
        error={problem ?? undefined}
      >
        {(id) => (
          <Input
            id={id}
            value={name}
            maxLength={NAME_MAX + 20}
            autoComplete="name"
            disabled={!online}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      <Field label="Email" hint="You sign in with this. It cannot be changed here yet.">
        {(id) => <Input id={id} value={user.email ?? ''} disabled readOnly />}
      </Field>
      <div className="sm:col-span-2">
        <Button type="submit" loading={busy} disabled={!online || !changed || !!problem}>
          Save name
        </Button>
      </div>
    </form>
  )
}

function Businesses() {
  const { memberships, current, selectTenant } = useWorkspace()
  const navigate = useNavigate()
  return (
    <ul className="grid gap-2 sm:col-span-2">
      {memberships.map((m) => {
        const here = m.tenant_id === current?.tenant_id
        return (
          <li
            key={m.tenant_id}
            className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5"
          >
            <span
              aria-hidden
              className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary font-extrabold text-on-fill"
            >
              {m.tenant.name.trim().charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <b className="block truncate text-sm" translate="no">
                {m.tenant.name}
              </b>
              <span className="block text-xs text-muted">{roleSentence(m.role)}</span>
            </div>
            {here ? (
              <Badge tone="success">Open now</Badge>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Switch to ${m.tenant.name}`}
                onClick={() => {
                  selectTenant(m.tenant_id)
                  navigate('/')
                }}
              >
                Switch
              </Button>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function PasswordDialog({ onClose }: { onClose: () => void }) {
  const user = useUser()
  const { changePassword } = useSession()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [signOutOthers, setSignOutOthers] = useState(true)
  const [wrong, setWrong] = useState(false)
  const [busy, setBusy] = useState(false)
  const strength = checkPassword(next, {
    name: accountName(user) ?? '',
    email: user.email ?? '',
  })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!strength.ok) return toast.error(strength.reason ?? 'Choose a password')
    setBusy(true)
    setWrong(false)
    try {
      await changePassword(current, next, { signOutOthers })
      toast.success(
        signOutOthers ? 'Password changed. Other devices are signed out.' : 'Password changed',
      )
      onClose()
    } catch (error) {
      if (error instanceof WrongPasswordError) setWrong(true)
      else toast.error(errorMessage(error))
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Change password"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="change-password" loading={busy} disabled={!current || !next}>
            Change password
          </Button>
        </>
      }
    >
      <form id="change-password" onSubmit={submit} className="grid gap-4">
        <Field
          label="Current password"
          error={wrong ? 'That is not your current password.' : undefined}
        >
          {(id) => (
            <PasswordInput
              id={id}
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value)
                setWrong(false)
              }}
              required
              autoComplete="current-password"
            />
          )}
        </Field>
        <Field label="New password">
          {(id) => (
            <>
              <PasswordInput
                id={id}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                autoComplete="new-password"
              />
              <PasswordStrength {...strengthOf(strength)} />
            </>
          )}
        </Field>
        <div>
          <Checkbox
            label="Sign out my other devices"
            checked={signOutOthers}
            onChange={(e) => setSignOutOthers(e.target.checked)}
          />
          <p className="mt-1 pl-6 text-xs text-subtle">
            Their saved data stays on them until someone signs in again.
          </p>
        </div>
      </form>
    </Modal>
  )
}
